import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import { useStore } from 'react-redux';
import { RootState } from 'src/store';
import { AlertsApi } from '../api';
import { 
  setLoading, 
  setList, 
  setFilter, 
  resetAlerts, 
  getAlertsLoading, 
  getAlertsFilter, 
  getAlertsList,
  addAlertOptimistic,
  updateAlertOptimistic,
  removeAlertOptimistic
} from '../slices';
import { AlertRequestData, GetAlertsParams } from '../types';

export const useAlertsService = () => {
  const dispatch = useDispatch();
  const { getState } = useStore<RootState>();
  const loading = useSelector(getAlertsLoading);
  const filter = useSelector(getAlertsFilter);
  const list = useSelector(getAlertsList);

  const getAlerts = useCallback(async (appendToList = false, isSearch = false) => {
    const currentState = getState().alerts;
    const filter = currentState.filter;
    const currentList = currentState.list;

    try {
      dispatch(setLoading(isSearch ? { search: true } : { getList: true }));

      const response = await AlertsApi.getAlerts({
        limit: filter.limit,
        offset: filter.offset,
        query: filter.query || undefined,
        sort: filter.sort,
        isActive: filter.isActive,
        mode: filter.mode,
        processMessages: filter.processMessages,
      });
      
      if (appendToList && currentList) {
        // Append new items to existing list (infinite scroll)
        const updatedList = {
          items: [...currentList.items, ...response.items],
          count: response.count
        };
        dispatch(setList(updatedList));
        return updatedList;
      } else {
        // Replace existing list (normal load/search)
        dispatch(setList(response));
        return response;
      }
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      throw error;
    } finally {
      dispatch(setLoading(isSearch ? { search: false } : { getList: false }));
    }
  }, [dispatch, getState]);

  const loadMoreAlerts = useCallback(async () => {
    const currentState = getState().alerts;
    const currentList = currentState.list;
    const currentFilter = currentState.filter;
    
    if (!currentList) return;
    
    const newOffset = currentList.items.length;
    
    try {
      // Don't use the main loading state for infinite scroll
      // Call API directly without affecting main loading state
      const response = await AlertsApi.getAlerts({
        ...currentFilter,
        offset: newOffset,
        query: currentFilter.query || undefined,
      });
      
      // Append new items to existing list
      const updatedList = {
        items: [...currentList.items, ...response.items],
        count: response.count
      };
      
      dispatch(setList(updatedList));
      return updatedList;
    } catch (error) {
      console.error('Failed to fetch more alerts:', error);
      throw error;
    }
  }, [dispatch, getState]);

  const changeAlertsFilter = useCallback((newFilter: Partial<GetAlertsParams>) => {
    dispatch(setFilter(newFilter));
  }, [dispatch]);

  const resetAlertsData = useCallback(() => {
    dispatch(resetAlerts());
  }, [dispatch]);

  // CRUD operations with optimistic updates
  const createAlert = useCallback(async (alertData: AlertRequestData) => {
    const currentState = getState().alerts;
    const currentList = currentState.list;
    
    try {
      dispatch(setLoading({ create: true }));
      const result = await AlertsApi.createAlert(alertData);
      
      // Add the newly created alert optimistically
      if (result && currentList) {
        dispatch(addAlertOptimistic(result));
      }
      
      return result;
    } catch (error) {
      console.error('Failed to create alert:', error);
      throw error;
    } finally {
      dispatch(setLoading({ create: false }));
    }
  }, [dispatch, getState]);

  const updateAlert = useCallback(async (alertId: string, alertData: AlertRequestData) => {
    const currentState = getState().alerts;
    const currentList = currentState.list;
    
    // Store original alert for potential rollback
    const originalAlert = currentList?.items.find(alert => alert.id === alertId);
    
    try {
      dispatch(setLoading({ update: true }));
      const result = await AlertsApi.updateAlert(alertId, alertData);
      
      // Update the alert optimistically
      if (result && currentList) {
        dispatch(updateAlertOptimistic(result));
      }
      
      return result;
    } catch (error) {
      console.error('Failed to update alert:', error);
      
      // Revert optimistic update if operation failed and we had original data
      if (originalAlert && currentList) {
        dispatch(updateAlertOptimistic(originalAlert));
      }
      
      throw error;
    } finally {
      dispatch(setLoading({ update: false }));
    }
  }, [dispatch, getState]);

  const deleteAlert = useCallback(async (alertId: string) => {
    const currentState = getState().alerts;
    const currentList = currentState.list;
    
    // Store original alert for potential rollback
    const originalAlert = currentList?.items.find(alert => alert.id === alertId);
    
    try {
      dispatch(setLoading({ delete: true }));
      
      // Optimistically remove the alert
      if (currentList && originalAlert) {
        dispatch(removeAlertOptimistic(alertId));
      }
      
      await AlertsApi.deleteAlert(alertId);
    } catch (error) {
      console.error('Failed to delete alert:', error);
      
      // Revert optimistic update if operation failed and we had original data
      if (originalAlert && currentList) {
        dispatch(addAlertOptimistic(originalAlert));
      }
      
      throw error;
    } finally {
      dispatch(setLoading({ delete: false }));
    }
  }, [dispatch, getState]);

  return {
    loading,
    filter,
    list,
    getAlerts,
    loadMoreAlerts,
    changeAlertsFilter,
    resetAlerts: resetAlertsData,
    createAlert,
    updateAlert,
    deleteAlert,
  };
}; 